import { sortObjectByKey } from '@utils/common';
import { getTechnologyColor } from '@utils/getTechnologyColor';
import { isEmptyObjectRecursive } from '@utils/objects/isEmptyObjectRecursive';

import { IBandOption } from '@interfaces/cells/cells';
import { TBandDTO } from '@interfaces/cells/dto/cellsDTO';
import { ISectorSettingDTO } from '@interfaces/map/dto/sectorsDTO';
import { ISectorsFilterState } from '@interfaces/map/sectors';
import { ICheckboxGroup } from '@components/common/NestedCheckboxGroup/NestedCheckboxGroup.types';

import { ETechnologiesLabels, ETechnologiesValues, TTechnologiesValues, TTechnologyValuesArray } from '@enums/common';

interface IPrepareNestedCheckboxes {
	bands: IBandOption[];
	currentFilterState: ISectorsFilterState | null;
	sectorSettings: ISectorSettingDTO[];
	usedBands: TBandDTO[];
	filteredTechnologiesOnMap: TTechnologyValuesArray | null;
}

export function prepareNestedCheckboxes({
	bands,
	currentFilterState,
	sectorSettings,
	usedBands,
	filteredTechnologiesOnMap,
}: IPrepareNestedCheckboxes): ICheckboxGroup[] {
	const usedBandNames = new Set(usedBands.map((usedBand) => usedBand.name));
	const sectorBandNames = new Set(sectorSettings.map((sectorSetting) => sectorSetting.band.name));
	const techMap: { [key in TTechnologiesValues]?: ICheckboxGroup[] } = {};
	const isEmptyFilters = isEmptyObjectRecursive(currentFilterState);

	bands.forEach((band) => {
		const { name, title, technology } = band;

		if (!usedBandNames.has(name) || !sectorBandNames.has(name) || !technology) return;

		const isTechnologyAvailableOnMap = (
			!filteredTechnologiesOnMap?.length ||
			filteredTechnologiesOnMap?.includes(technology)
		);

		const checked = !!(
			isTechnologyAvailableOnMap &&
			(isEmptyFilters || currentFilterState?.bands.includes(name))
		);

		const option: ICheckboxGroup = {
			checked,
			label: title,
			isCollapsed: true,
			value: name,
			disabled: !isTechnologyAvailableOnMap,
		};

		techMap[technology] = techMap[technology] || [];
		techMap[technology]!.push(option);
	});

	const result: ICheckboxGroup[] = Object.entries(techMap).map(([technology, options]) => {
		const allChecked = options.every((option: ICheckboxGroup) => option.checked);
		const anyChecked = options.some((option: ICheckboxGroup) => option.checked);
		const isTechnologyAvailableOnMap = (
			!filteredTechnologiesOnMap?.length ||
			filteredTechnologiesOnMap?.includes(technology as TTechnologiesValues)
		);
		const checked = !!(
			isTechnologyAvailableOnMap &&
			(isEmptyFilters || currentFilterState?.technologies.includes(technology as TTechnologiesValues))
		);

		return {
			checked,
			disabled: !isTechnologyAvailableOnMap,
			isIndefinite: !allChecked && anyChecked,
			color: getTechnologyColor(technology as TTechnologiesValues),
			isCollapsed: true,
			label: ETechnologiesLabels[technology as keyof typeof ETechnologiesValues],
			nestedItems: options,
			value: technology,
		};
	});

	return sortObjectByKey(result, 'label') || [];
}
